import BackButton from "components/BackButton";
import ConnectionStatus from "components/ConnectionStatus";
import BasicButton from "components/General/BasicButton";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useWebSocket } from "WebSocketContext";
import { useVowGameContext } from "VowGameContext";
import { ROLE_NAMES, createPlayers, type PlayerKey } from "./vowGameLogic";

const ROLE_ORDER: PlayerKey[] = ["herder", "reed_harvester", "hunter", "protector", "bittern"];
const ROLE_DETAILS = createPlayers();

const VowSelectRole: React.FC = () => {
  const navigate = useNavigate();
  const { players, playerId, assignSelfRole, selfRole, isHost } = useVowGameContext();
  const { isConnected } = useWebSocket();
  const wasConnectedRef = useRef(isConnected);
  const wasHostRef = useRef(isHost);

  useEffect(() => {
    if (isHost) {
      navigate("/vow", { replace: true });
    }
  }, [isHost, navigate]);

  useEffect(() => {
    if (isHost) {
      wasHostRef.current = true;
    }
  }, [isHost]);

  useEffect(() => {
    if (isConnected) {
      wasConnectedRef.current = true;
      return;
    }
    if (!wasConnectedRef.current) {
      return;
    }

    navigate(wasHostRef.current ? "/vow/host" : "/vow/join", { replace: true });
  }, [isConnected, navigate]);

  const selfPlayer = useMemo(
    () => players.find((player) => player.id === playerId),
    [players, playerId]
  );
  const roleLocked = Boolean(selfPlayer?.roleLocked);

  const [nameInput, setNameInput] = useState(selfPlayer?.playerName ?? "");

  useEffect(() => {
    setNameInput(selfPlayer?.playerName ?? "");
  }, [selfPlayer?.playerName]);

  const takenRoles = useMemo(() => {
    const taken = new Set<PlayerKey>();
    players.forEach((player) => {
      if (player.id !== playerId && player.role) {
        taken.add(player.role as PlayerKey);
      }
    });
    return taken;
  }, [players, playerId]);

  const [selectedRole, setSelectedRole] = useState<PlayerKey | null>(selfRole ?? null);

  useEffect(() => {
    if (selfRole) {
      setSelectedRole(selfRole);
    }
  }, [selfRole]);

  useEffect(() => {
    if (roleLocked && selfRole) {
      setSelectedRole(selfRole);
    }
  }, [roleLocked, selfRole]);

  const isRoleUnavailable =
    !roleLocked &&
    selectedRole !== null &&
    selectedRole !== selfRole &&
    takenRoles.has(selectedRole);

  const handleConfirm = () => {
    if (!selectedRole) {
      return;
    }

    if (isRoleUnavailable) {
      return;
    }

    if (roleLocked && selfRole && selectedRole !== selfRole) {
      return;
    }

    const displayName = nameInput.trim() || `Player ${playerId}`;
    const confirmedRole = roleLocked && selfRole ? selfRole : selectedRole;
    assignSelfRole(confirmedRole, displayName);
    navigate("/vow");
  };

  if (isHost) {
    return null;
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-[#f3eddb] px-4 py-10">
      <ConnectionStatus />

      <div className="w-full max-w-4xl space-y-6 border-2 border-black bg-[#BCB7A8] p-8">
        <div className="flex flex-col gap-2 text-center">
          <h1 className="text-3xl font-bold text-slate-900">Choose Your VOW Role</h1>
          <p className="text-base text-slate-800">
            Pick a role and set your display name before entering the marsh. Roles already taken by
            other players are shown as unavailable.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="flex flex-col gap-4 border-2 border-black bg-[#dcd2b5] p-6">
            <label className="text-sm font-semibold text-slate-800" htmlFor="display-name">
              Display Name
            </label>
            <input
              id="display-name"
              type="text"
              value={nameInput}
              onChange={(event) => setNameInput(event.target.value)}
              className="w-full border border-black bg-[#faf3e2] px-3 py-2 text-base text-slate-900 outline-none focus:ring-2 focus:ring-black"
              placeholder={`Player ${playerId}`}
            />

            <div>
              <p className="text-sm font-semibold text-slate-800">Current Assignment</p>
              <p className="mt-1 border border-black bg-[#faf3e2] px-3 py-2 text-base font-semibold text-slate-900">
                {selfRole ? ROLE_NAMES[selfRole] : "No role selected"}
              </p>
            </div>

            <div className="border-2 border-black bg-[#faf3e2]">
              <p className="border-b border-black px-3 py-2 text-left text-xs uppercase tracking-wide text-slate-700">
                Players in Lobby
              </p>
              <ul className="divide-y divide-black/30 text-left text-sm">
                {players.length === 0 && (
                  <li className="px-3 py-2 text-slate-700">Waiting for other players…</li>
                )}
                {players.map((player) => (
                  <li key={player.id} className="flex items-center justify-between px-3 py-2">
                    <span className="font-medium text-slate-900">
                      {player.playerName || `Player ${player.id}`}
                    </span>
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                      {player.role ? ROLE_NAMES[player.role as PlayerKey] : "No role"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="flex flex-col gap-4 border-2 border-black bg-[#dcd2b5] p-6">
            <p className="text-sm font-semibold text-slate-800">Available Roles</p>
            {roleLocked && (
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                Role locked after confirmation.
              </p>
            )}
            <div className="grid gap-3">
              {ROLE_ORDER.map((role) => {
                const isSelected = selectedRole === role;
                const isTakenByOthers = takenRoles.has(role);
                const disabled = roleLocked ? role !== selfRole : (isTakenByOthers && role !== selfRole);

                return (
                  <button
                    key={role}
                    type="button"
                    onClick={() => {
                      if (roleLocked) return;
                      setSelectedRole(role);
                    }}
                    disabled={disabled}
                    className={`flex flex-col items-start gap-1 border-2 border-black px-4 py-3 text-left transition-colors ${
                      isSelected
                        ? "bg-[#d7cfb2]"
                        : "bg-[#faf3e2] hover:bg-[#e7dec7]"
                    } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
                  >
                    <div className="flex w-full items-center justify-between">
                      <span className="text-base font-semibold text-slate-900">{ROLE_NAMES[role]}</span>
                      {disabled && (
                        <span className="text-xs font-semibold uppercase text-rose-600">Taken</span>
                      )}
                      {isSelected && (
                        <span className="text-xs font-semibold uppercase text-emerald-600">Selected</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-600">{ROLE_DETAILS[role].objective}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center gap-4 md:flex-row md:justify-end">
          <BackButton useAbsolute={false} />
          {isRoleUnavailable && (
            <p className="text-sm font-medium text-rose-600">
              That role is already taken by another player.
            </p>
          )}
          <BasicButton
            text="Confirm & Enter Game"
            onClick={handleConfirm}
            className={`px-6 py-3 text-base ${
              !selectedRole || isRoleUnavailable ? "cursor-not-allowed opacity-50" : ""
            }`}
          />
        </div>
      </div>
    </div>
  );
};

export default VowSelectRole;
